import { QueryClient, InfiniteData } from '@tanstack/react-query';

type PhotoLikeObject = {
  photoId: string;
};

type PhotosPage<T extends PhotoLikeObject> = {
  photos: T[];
  [key: string]: unknown;
};

function updatePhotosArray<T extends PhotoLikeObject>(
  photos: T[] | undefined,
  photoId: string,
  updater: (photo: T) => T
): { updated: boolean; photos?: T[] } {
  if (!photos) {
    return { updated: false };
  }

  let changed = false;
  const nextPhotos = photos.map((item) => {
    if (item.photoId !== photoId) {
      return item;
    }
    changed = true;
    return updater(item);
  });
  return changed ? { updated: true, photos: nextPhotos } : { updated: false };
}

export function updatePhotoInAllCaches<T extends PhotoLikeObject>(
  queryClient: QueryClient,
  photoId: string,
  updater: (photo: T) => T
) {
  // Update listing/infinite queries
  queryClient.setQueriesData<InfiniteData<PhotosPage<T>> | PhotosPage<T>>(
    {
      predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === 'photos',
    },
    (oldData) => {
      if (!oldData) return oldData;

      // Handle infinite query shape
      if ('pages' in oldData && Array.isArray(oldData.pages)) {
        let modified = false;
        const nextPages = oldData.pages.map((page) => {
          const { updated, photos } = updatePhotosArray(page.photos as T[] | undefined, photoId, updater);
          if (updated && photos) {
            modified = true;
            return {
              ...page,
              photos,
            };
          }
          return page;
        });
        return modified
          ? {
              ...oldData,
              pages: nextPages as typeof oldData.pages,
            }
          : oldData;
      }

      // Handle simple object with photos array
      if ('photos' in oldData) {
        const page = oldData as PhotosPage<T>;
        const { updated, photos } = updatePhotosArray(page.photos, photoId, updater);
        if (updated && photos) {
          return {
            ...oldData,
            photos,
          } as PhotosPage<T>;
        }
      }

      return oldData;
    }
  );

  // Update individual photo detail cache
  queryClient.setQueryData<T>(['photo-detail', photoId], (existing) => {
    if (!existing) return existing;
    return updater(existing);
  });
}
